import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import type { DashboardOccupancyBreakdownEntry } from "./types";

export interface DashboardOccupancyPanelProps {
  entries: DashboardOccupancyBreakdownEntry[];
  title: string;
  bookingsLabel: string;
  emptyLabel: string;
  className?: string;
}

export function DashboardOccupancyPanel({
  entries,
  title,
  bookingsLabel,
  emptyLabel,
  className,
}: DashboardOccupancyPanelProps) {
  const reduceMotion = useReducedMotion();
  return (
    <section
      className={cn(
        "flex flex-col rounded-biz-card border border-biz-border bg-biz-surface-elevated biz-shadow-sm",
        className,
      )}
      aria-labelledby="dash-occ-title"
    >
      <header className="border-b border-biz-border px-4 py-3">
        <h3 id="dash-occ-title" className="text-sm font-semibold text-biz-foreground">
          {title}
        </h3>
      </header>
      {entries.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-biz-foreground-muted">{emptyLabel}</div>
      ) : (
        <ul className="flex flex-col gap-3 p-4" role="list">
          {entries.map((e) => {
            const pct = Math.round(Math.max(0, Math.min(1, e.utilisation)) * 100);
            const tone =
              pct >= 85
                ? "bg-biz-success"
                : pct >= 60
                ? "bg-biz-primary"
                : pct >= 35
                ? "bg-biz-info"
                : "bg-biz-warning";
            return (
              <li key={e.serviceKey}>
                <div className="mb-1 flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-medium text-biz-foreground">
                    {e.serviceLabel}
                  </span>
                  <span className="font-mono-num text-xs text-biz-foreground-muted">
                    <span className="font-semibold text-biz-foreground">{pct}%</span>
                    {" · "}
                    {e.bookings} {bookingsLabel}
                  </span>
                </div>
                <div
                  className="h-2 w-full overflow-hidden rounded-full bg-biz-muted"
                  role="progressbar"
                  aria-valuenow={pct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`${e.serviceLabel} ${pct}%`}
                >
                  <motion.div
                    className={cn("h-full rounded-full", tone, "biz-motion-safe")}
                    initial={reduceMotion ? false : { width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
